
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import DataTable from "./DataTable";

// Create a lightweight logging utility that only logs in development
const isDevelopment = import.meta.env.DEV;
const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  }
};

const CoursesAdmin = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      logger.log("Fetched courses:", data);
      setCourses(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load courses",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (course: any) => {
    if (!confirm("Are you sure you want to delete this course?")) return;

    try {
      const { error } = await supabase
        .from("courses")
        .delete()
        .match({ id: course.id });

      if (error) throw error;

      toast({
        title: "Course deleted",
        description: "The course has been successfully deleted.",
      });

      await fetchCourses();
    } catch (error: any) {
      toast({
        title: "Failed to delete course",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const columns = [
    {
      key: "title",
      label: "Title",
    },
    {
      key: "level",
      label: "Level",
      format: (value: string) => (
        <Badge variant="outline">{value}</Badge>
      ),
    },
    {
      key: "text_direction",
      label: "Direction",
      format: (value: string) => (
        <Badge variant="outline">{value?.toUpperCase() || "LTR"}</Badge>
      ),
    },
    {
      key: "price",
      label: "Price",
    },
    {
      key: "status",
      label: "Status",
      format: (value: string) => (
        <Badge variant={value === "Enrolling" ? "default" : "secondary"}>
          {value || "Draft"}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Courses</h2>
        <Button
          size="sm"
          onClick={() => navigate("/courses/create")}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" /> Add Course
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading courses...</div>
        </div>
      ) : (
        <DataTable
          data={courses}
          columns={columns}
          onView={(course) => window.open(`/courses/${course.id}`, "_blank")}
          onEdit={(course) => navigate(`/courses/edit/${course.id}`)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default CoursesAdmin;
